import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import {
  Search, RefreshCw, Loader2, Trash2, ChevronLeft, ChevronRight, X, Send,
  CheckCircle2, Copy, Megaphone, Users, AlertTriangle,
} from "lucide-react";
import { adminAxios, relativeTime } from "./adminApi";

export default function AdminWaitlist() {
  const [data, setData] = useState({ items: [], total: 0, page: 1, total_pages: 1 });
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [confirmBulk, setConfirmBulk] = useState(false);
  const [groups, setGroups] = useState([]);
  const [groupsLoading, setGroupsLoading] = useState(true);
  const [notifyTarget, setNotifyTarget] = useState(null); // group being notified
  const [notifying, setNotifying] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await adminAxios().get("/waitlist", { params: { q: q || undefined, page, page_size: 20 } });
      setData(r.data);
      setSelected(new Set());
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to load waitlist"); }
    finally { setLoading(false); }
  }, [q, page]);

  const loadGroups = useCallback(async () => {
    setGroupsLoading(true);
    try {
      const r = await adminAxios().get("/waitlist/by-category");
      setGroups(r.data.groups || []);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to load category groups"); }
    finally { setGroupsLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadGroups(); }, [loadGroups]);

  const toggle = (email) => {
    const next = new Set(selected);
    next.has(email) ? next.delete(email) : next.add(email);
    setSelected(next);
  };
  const toggleAll = () => {
    if (selected.size === data.items.length) setSelected(new Set());
    else setSelected(new Set(data.items.map((i) => i.email)));
  };

  const doBulkDelete = async () => {
    if (selected.size === 0) return;
    try {
      const r = await adminAxios().post("/waitlist/bulk-delete", { emails: Array.from(selected) });
      toast.success(`Deleted ${r.data.deleted} entries`);
      setConfirmBulk(false);
      load();
      loadGroups();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  const doNotifyCategory = async () => {
    if (!notifyTarget) return;
    setNotifying(true);
    try {
      const r = await adminAxios().post("/waitlist/notify-category", {
        category_id: notifyTarget.category_id,
        category_label: notifyTarget.category_label,
      });
      toast.success(
        r.data.sent > 0
          ? `Sent to ${r.data.sent} ${r.data.sent === 1 ? "person" : "people"}${r.data.failed ? ` · ${r.data.failed} failed` : ""}`
          : "Nobody left to notify in this category.",
      );
      setNotifyTarget(null);
      loadGroups();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Notify failed");
    } finally { setNotifying(false); }
  };

  const copyInvite = async (row) => {
    const base = window.location.origin;
    const text = `Hi! You joined our category waitlist for "${row.category_label || row.category_id}". We're now inviting people from your niche — sign up at ${base}/signup?utm=waitlist_invite&cat=${encodeURIComponent(row.category_id || "")}`;
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Invite text copied to clipboard");
      await adminAxios().post("/waitlist/mark-invited", { email: row.email });
      load();
    } catch { toast.error("Clipboard copy failed"); }
  };

  const deleteOne = async (row) => {
    try {
      await adminAxios().post("/waitlist/bulk-delete", { emails: [row.email] });
      toast.success("Deleted");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div data-testid="admin-waitlist">
      <div className="mb-6">
        <div className="text-[11px] font-mono uppercase tracking-widest text-lm-secondary">Waitlist</div>
        <h1 className="mt-1 text-2xl md:text-3xl font-display font-semibold tracking-tight text-lm-text">Category waitlist</h1>
        <p className="text-sm text-lm-text2 mt-1">Tier-3 declined signups. {data.total} entries total.</p>
      </div>

      {/* Category groups — one card per industry with a bulk-notify button.
          Only rendered when we actually have groups (avoids an empty section). */}
      <div className="mb-6" data-testid="waitlist-category-groups">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-[11px] font-mono uppercase tracking-widest text-lm-secondary">Notify a whole category</div>
            <h2 className="text-lg font-display font-semibold tracking-tight text-lm-text mt-0.5">Bulk-launch a waitlist</h2>
          </div>
          <button
            onClick={loadGroups}
            data-testid="groups-refresh"
            className="inline-flex items-center gap-1.5 text-xs bg-lm-tint text-lm-text2 border border-lm-border rounded-md px-3 py-1.5 hover:bg-lm-tint2"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${groupsLoading ? "animate-spin" : ""}`} /> Refresh
          </button>
        </div>
        {groupsLoading ? (
          <div className="bg-lm-surface border border-lm-border rounded-lg px-4 py-8 text-center text-sm text-lm-text2">
            <Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading categories…
          </div>
        ) : groups.length === 0 ? (
          <div className="bg-lm-surface border border-lm-border rounded-lg px-4 py-8 text-center text-sm text-lm-text3">
            No categories on the waitlist yet.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
            {groups.map((g) => {
              const fullyNotified = g.pending_count === 0 && g.notified_count > 0;
              const partiallyNotified = g.notified_count > 0 && g.pending_count > 0;
              return (
                <div
                  key={g.category_id || "_uncategorised"}
                  data-testid={`waitlist-group-${g.category_id || "_uncategorised"}`}
                  className="bg-lm-surface border border-lm-border rounded-xl p-4 flex flex-col gap-3"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="font-display font-semibold text-lm-text tracking-tight text-base truncate">
                        {g.category_label || g.category_id}
                      </div>
                      <div className="text-[11px] font-mono uppercase tracking-widest text-lm-text3 mt-0.5">
                        First signup {relativeTime(g.first_signup_at)}
                      </div>
                    </div>
                    <span className="inline-flex items-center gap-1 text-xs font-mono px-2 py-0.5 bg-lm-tint2 text-lm-primary rounded border border-lm-tint2 shrink-0">
                      <Users className="w-3 h-3" /> {g.total}
                    </span>
                  </div>

                  <div className="flex items-center justify-between gap-2">
                    <div className="text-xs text-lm-text2">
                      {fullyNotified ? (
                        <span className="inline-flex items-center gap-1 text-emerald-700">
                          <CheckCircle2 className="w-3.5 h-3.5" /> Notified {relativeTime(g.last_notified_at)}
                        </span>
                      ) : partiallyNotified ? (
                        <span className="inline-flex items-center gap-1 text-amber-700">
                          <AlertTriangle className="w-3.5 h-3.5" /> {g.notified_count} sent · {g.pending_count} pending
                        </span>
                      ) : (
                        <span className="text-lm-text3">{g.pending_count} waiting</span>
                      )}
                    </div>
                    <button
                      onClick={() => setNotifyTarget(g)}
                      disabled={fullyNotified}
                      data-testid={`notify-btn-${g.category_id || "_uncategorised"}`}
                      className={
                        fullyNotified
                          ? "inline-flex items-center gap-1.5 text-xs bg-lm-tint text-lm-text3 border border-lm-border rounded-md px-3 py-1.5 cursor-not-allowed"
                          : "inline-flex items-center gap-1.5 text-xs bg-lm-primary text-white rounded-md px-3 py-1.5 hover:bg-lm-primaryHover"
                      }
                    >
                      <Megaphone className="w-3.5 h-3.5" />
                      {fullyNotified ? "Already notified" : partiallyNotified ? `Notify ${g.pending_count} more` : "Notify waitlist"}
                    </button>
                  </div>

                  {g.last_notified_by && (
                    <div className="text-[10px] font-mono uppercase tracking-widest text-lm-text3 border-t border-lm-border pt-2">
                      Last sent by {g.last_notified_by}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="bg-lm-surface border border-lm-border rounded-lg p-3 mb-4 flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-lm-text3" />
          <input
            data-testid="waitlist-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && (setPage(1), load())}
            placeholder="Search by email or category…"
            className="w-full pl-9 pr-3 py-2 rounded-md border border-lm-border bg-lm-bg text-sm focus:outline-none focus:ring-2 focus:ring-lm-primary/20"
          />
        </div>
        <button onClick={() => (setPage(1), load())} data-testid="waitlist-refresh" className="inline-flex items-center gap-1.5 text-xs bg-lm-tint text-lm-text2 border border-lm-border rounded-md px-3 py-2 hover:bg-lm-tint2">
          <RefreshCw className="w-3.5 h-3.5" /> Refresh
        </button>
        {selected.size > 0 && (
          <button
            onClick={() => setConfirmBulk(true)}
            data-testid="waitlist-bulk-delete"
            className="inline-flex items-center gap-1.5 text-xs bg-red-600 text-white rounded-md px-3 py-2 hover:bg-red-700"
          >
            <Trash2 className="w-3.5 h-3.5" /> Delete {selected.size} selected
          </button>
        )}
      </div>

      <div className="bg-lm-surface border border-lm-border rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-lm-tint/60 border-b border-lm-border text-lm-text3">
              <tr>
                <th className="text-left px-4 py-2.5 w-10">
                  <input
                    type="checkbox"
                    data-testid="waitlist-select-all"
                    checked={data.items.length > 0 && selected.size === data.items.length}
                    onChange={toggleAll}
                  />
                </th>
                <th className="text-left px-4 py-2.5 font-medium">Email</th>
                <th className="text-left px-4 py-2.5 font-medium">Category</th>
                <th className="text-right px-4 py-2.5 font-medium">When</th>
                <th className="text-right px-4 py-2.5 font-medium">Invited</th>
                <th className="text-right px-4 py-2.5 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} className="px-4 py-12 text-center text-lm-text2"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading…</td></tr>
              ) : data.items.length === 0 ? (
                <tr><td colSpan={6} className="px-4 py-12 text-center text-lm-text3">No entries.</td></tr>
              ) : data.items.map((w) => (
                <tr key={w.email + w.created_at} data-testid={`waitlist-row-${w.email}`} className="border-t border-lm-border/60">
                  <td className="px-4 py-2.5">
                    <input
                      type="checkbox"
                      data-testid={`waitlist-check-${w.email}`}
                      checked={selected.has(w.email)}
                      onChange={() => toggle(w.email)}
                    />
                  </td>
                  <td className="px-4 py-2.5">
                    <div className="font-medium">{w.email || "—"}</div>
                  </td>
                  <td className="px-4 py-2.5">
                    <span className="text-xs font-mono px-2 py-0.5 bg-lm-tint2 text-lm-primary rounded border border-lm-tint2">
                      {w.category_label || w.category_id || "—"}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-right text-xs font-mono text-lm-text3">{relativeTime(w.created_at)}</td>
                  <td className="px-4 py-2.5 text-right">
                    {w.invited_at ? (
                      <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200">
                        <CheckCircle2 className="w-3 h-3" /> Invited
                      </span>
                    ) : (
                      <span className="text-[10px] font-mono uppercase tracking-widest text-lm-text3">—</span>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    <div className="inline-flex items-center gap-1">
                      <button
                        onClick={() => copyInvite(w)}
                        data-testid={`waitlist-invite-${w.email}`}
                        title="Copy invite text & mark as invited"
                        className="p-1.5 rounded text-lm-text2 hover:text-lm-primary hover:bg-lm-tint2"
                      >
                        <Copy className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => deleteOne(w)}
                        data-testid={`waitlist-delete-${w.email}`}
                        title="Delete entry"
                        className="p-1.5 rounded text-red-600 hover:bg-red-50"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between px-4 py-3 border-t border-lm-border text-xs text-lm-text3 font-mono">
          <div>Page {data.page} of {data.total_pages} · {data.total} entries</div>
          <div className="flex items-center gap-1">
            <button
              data-testid="waitlist-prev"
              disabled={data.page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="p-1.5 rounded border border-lm-border hover:bg-lm-tint disabled:opacity-40"
            >
              <ChevronLeft className="w-3.5 h-3.5" />
            </button>
            <button
              data-testid="waitlist-next"
              disabled={data.page >= data.total_pages}
              onClick={() => setPage((p) => Math.min(data.total_pages, p + 1))}
              className="p-1.5 rounded border border-lm-border hover:bg-lm-tint disabled:opacity-40"
            >
              <ChevronRight className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </div>

      {confirmBulk && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-xl max-w-md w-full shadow-2xl">
            <div className="flex items-center justify-between px-5 py-3 border-b border-lm-border">
              <h3 className="font-display font-semibold">Delete {selected.size} entries?</h3>
              <button onClick={() => setConfirmBulk(false)} className="p-1 hover:bg-lm-tint rounded"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-5">
              <p className="text-sm text-lm-text2">This permanently removes {selected.size} waitlist entries. Cannot be undone.</p>
              <div className="mt-4 flex justify-end gap-2">
                <button onClick={() => setConfirmBulk(false)} className="px-4 py-2 text-sm border border-lm-border rounded-md hover:bg-lm-tint">Cancel</button>
                <button
                  data-testid="waitlist-bulk-confirm"
                  onClick={doBulkDelete}
                  className="px-4 py-2 text-sm bg-red-600 text-white rounded-md hover:bg-red-700 inline-flex items-center gap-1.5"
                >
                  <Trash2 className="w-3.5 h-3.5" /> Delete all
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {notifyTarget && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-xl max-w-md w-full shadow-2xl" data-testid="notify-modal">
            <div className="flex items-center justify-between px-5 py-3 border-b border-lm-border">
              <h3 className="font-display font-semibold inline-flex items-center gap-2">
                <Megaphone className="w-4 h-4 text-lm-primary" /> Notify waitlist
              </h3>
              <button
                onClick={() => setNotifyTarget(null)}
                data-testid="notify-modal-close"
                className="p-1 hover:bg-lm-tint rounded"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-5">
              <p className="text-sm text-lm-text">
                Send launch email to{" "}
                <strong>{notifyTarget.pending_count} {notifyTarget.pending_count === 1 ? "person" : "people"}</strong>{" "}
                waiting for <strong>{notifyTarget.category_label || notifyTarget.category_id}</strong>?
              </p>
              <div className="mt-3 rounded-lg border border-lm-border bg-lm-tint/40 p-3 text-xs text-lm-text2 font-mono leading-relaxed">
                <div className="text-[10px] uppercase tracking-widest text-lm-text3 mb-1">Preview</div>
                <div><strong>Subject:</strong> Leadmato now supports {notifyTarget.category_label || notifyTarget.category_id}</div>
                <div className="mt-1"><strong>CTA:</strong> Sign up & run a free scan → /signup</div>
              </div>
              {notifyTarget.notified_count > 0 && (
                <p className="mt-3 text-xs text-amber-700 inline-flex items-center gap-1">
                  <AlertTriangle className="w-3.5 h-3.5" />
                  {notifyTarget.notified_count} in this category already received the launch email — they will be skipped.
                </p>
              )}
              <div className="mt-4 flex justify-end gap-2">
                <button
                  onClick={() => setNotifyTarget(null)}
                  disabled={notifying}
                  className="px-4 py-2 text-sm border border-lm-border rounded-md hover:bg-lm-tint disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={doNotifyCategory}
                  disabled={notifying || notifyTarget.pending_count === 0}
                  data-testid="notify-confirm-btn"
                  className="px-4 py-2 text-sm bg-lm-primary text-white rounded-md hover:bg-lm-primaryHover disabled:opacity-60 inline-flex items-center gap-1.5"
                >
                  {notifying ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                  {notifying ? "Sending…" : `Send to ${notifyTarget.pending_count}`}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
